import { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Activity, Users, ShieldCheck, FileCheck2, ArrowUpRight } from "lucide-react";
import { getOverview } from "../lib/api";

function Kpi({ icon: Icon, tint, label, value, sub }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-[#EAE1D3] bg-white p-5 shadow-[0_1px_2px_rgba(44,36,27,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(44,36,27,0.35)]" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-40 blur-2xl transition-opacity group-hover:opacity-70" style={{ background: tint.bg }} />
      <div className="relative flex items-start justify-between">
        <span className="grid h-11 w-11 place-items-center rounded-xl shadow-inner" style={{ background: tint.bg }}>
          <Icon size={19} style={{ color: tint.fg }} />
        </span>
        <span className="rounded-full bg-[#F5F1E8] px-2 py-0.5 font-body text-[10px] font-semibold uppercase tracking-wide text-[#A79C8A]">live</span>
      </div>
      <p className="relative mt-4 font-heading text-[40px] leading-none font-semibold text-[#2C241B]" data-testid={`kpi-value-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
      <p className="relative mt-2 font-body text-[13px] font-semibold text-[#5C5449]">{label}</p>
      <p className="relative mt-0.5 font-body text-xs text-[#A79C8A]">{sub}</p>
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    getOverview().then((d) => alive && setData(d));
    return () => { alive = false; };
  }, []);

  const k = data?.kpis || {};
  const trend = data?.trend || [];
  const signals = data?.signals || [];
  const tickEvery = Math.ceil(trend.length / 8);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-overview">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-body text-[12px] font-semibold uppercase tracking-[0.14em] text-[#C1440E]">Live operations</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-[#2C241B] md:text-[38px]">Marketplace overview</h1>
          <p className="mt-2 max-w-2xl font-body text-sm text-[#8A8072]">A current view of demand, supply, engagement, and published content.</p>
        </div>
        <button className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#D84315] to-[#E86A3A] px-4 py-2.5 font-body text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(216,67,21,0.8)] transition-all hover:shadow-[0_10px_26px_-8px_rgba(216,67,21,0.95)] active:scale-[0.98]" data-testid="admin-create-page-btn">
          Create page <ArrowUpRight size={16} />
        </button>
      </div>

      {/* KPI cards */}
      <div className="pc-stagger mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi icon={Activity} tint={{ bg: "#FBE4D8", fg: "#D84315" }} label="Daily active users" value={k.dau ?? 0} sub={`${k.dauWeek ?? 0} active this week`} />
        <Kpi icon={Users} tint={{ bg: "#E7EFD6", fg: "#6D8B44" }} label="Monthly active users" value={k.mau ?? 0} sub={`${k.mauSessions ?? 0} sessions in 30 days`} />
        <Kpi icon={ShieldCheck} tint={{ bg: "#DBEDE9", fg: "#2F7A6B" }} label="Verified priests" value={k.verifiedPriests ?? 0} sub={`${k.openRequests ?? 0} open ceremony requests`} />
        <Kpi icon={FileCheck2} tint={{ bg: "#EAE3F5", fg: "#6A4EA8" }} label="Published pages" value={k.publishedPages ?? 0} sub={`${k.activeAccounts ?? 0} active accounts`} />
      </div>

      {/* Trend + signals */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-[#EAE1D3] bg-white p-5 shadow-[0_1px_2px_rgba(44,36,27,0.04)] lg:col-span-2" data-testid="admin-trend-chart">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-body text-[12px] font-semibold uppercase tracking-[0.14em] text-[#C1440E]">30-day trend</p>
              <h2 className="mt-0.5 font-heading text-xl font-semibold text-[#2C241B]">Demand and activity</h2>
            </div>
            <div className="flex items-center gap-4 font-body text-xs text-[#8A8072]">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#D84315]" /> Active users</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#2C241B]" /> Requests</span>
            </div>
          </div>
          <div className="mt-5 h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
              <LineChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#F0EBE1" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval={tickEvery} tick={{ fill: "#A79C8A", fontSize: 11 }} />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} tick={{ fill: "#A79C8A", fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E9E2D6", fontSize: 12 }} />
                <Line type="monotone" dataKey="activeUsers" stroke="#D84315" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="requests" stroke="#2C241B" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-[#EAE1D3] bg-white p-5 shadow-[0_1px_2px_rgba(44,36,27,0.04)]" data-testid="admin-product-signals">
          <div className="flex items-center justify-between">
            <p className="font-body text-[12px] font-semibold uppercase tracking-[0.14em] text-[#C1440E]">Product signals</p>
            <button className="font-body text-xs font-semibold text-[#D84315] hover:underline">View report</button>
          </div>
          <h2 className="mt-0.5 font-heading text-xl font-semibold text-[#2C241B]">Most-used features</h2>
          <ul className="mt-5 space-y-4">
            {signals.length === 0 ? (
              <li className="rounded-xl border border-dashed border-[#E9E2D6] bg-[#FAFAF5] px-4 py-6 text-center font-body text-sm text-[#A79C8A]">
                No signals yet — data appears once the app has traffic.
              </li>
            ) : (
              signals.map((s, i) => (
                <li key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-body text-xs tabular-nums text-[#C7BEAF]">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-body text-sm font-semibold text-[#2C241B]">{s.name}</p>
                      <p className="font-body text-xs text-[#A79C8A]">{s.users} users · {s.sessions} sessions</p>
                    </div>
                  </div>
                  <span className="font-heading text-lg font-semibold text-[#2C241B]">{s.count}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
